import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";

export default function CookiePolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--liturgical-primary)] hover:underline mb-8 font-['Nunito']">
          <ArrowLeft className="w-4 h-4" /> Back to Parish
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E0D5] p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Cookie className="w-8 h-8 text-[var(--liturgical-primary)]" />
            <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">Cookie Policy</h1>
          </div>
          
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-8">Last Updated: February 25, 2026</p>
          
          <div className="prose prose-slate max-w-none font-['Nunito'] text-[#2D2A26]">
            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">1. What Are Cookies?</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit websites. They are widely used to make websites work more efficiently and to provide information to website owners. Cookies help us provide you with a better experience by enabling us to recognize you, remember your preferences, and understand how you use our platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">2. Types of Cookies We Use</h2>
              
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse border border-[#E5E0D5] text-sm">
                  <thead>
                    <tr className="bg-[#F9F7F2]">
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Type</th>
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Purpose</th>
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-[#E5E0D5] p-3 font-medium">Essential Cookies</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Required for the platform to function. Enable core features like authentication, security, and account access.</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Session / 30 days</td>
                    </tr>
                    <tr className="bg-[#FAFAFA]">
                      <td className="border border-[#E5E0D5] p-3 font-medium">Preference Cookies</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Remember your settings and preferences, such as language, saved location, and display preferences.</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">1 year</td>
                    </tr>
                    <tr>
                      <td className="border border-[#E5E0D5] p-3 font-medium">Analytics Cookies</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Help us understand how visitors interact with Parish, which pages are visited, and how users navigate the platform.</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">3. Specific Cookies We Use</h2>
              
              <h3 className="font-semibold text-[#2D2A26] mt-4 mb-2">3.1 Essential Cookies</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-[#E5E0D5] text-sm">
                  <thead>
                    <tr className="bg-[#F9F7F2]">
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Cookie Name</th>
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-[#E5E0D5] p-3 font-mono text-xs">token</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Authentication token to keep you logged in</td>
                    </tr>
                    <tr className="bg-[#FAFAFA]">
                      <td className="border border-[#E5E0D5] p-3 font-mono text-xs">user</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">Stores basic user information for quick access</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="font-semibold text-[#2D2A26] mt-6 mb-2">3.2 Analytics Cookies (PostHog)</h3>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                We use PostHog for analytics to understand how users interact with Parish. PostHog may set the following cookies:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-[#E5E0D5] text-sm">
                  <thead>
                    <tr className="bg-[#F9F7F2]">
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Cookie Name</th>
                      <th className="border border-[#E5E0D5] p-3 text-left font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-[#E5E0D5] p-3 font-mono text-xs">ph_*</td>
                      <td className="border border-[#E5E0D5] p-3 text-[#4A4A4A]">PostHog analytics identifiers and session tracking</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">4. Third-Party Cookies</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. Third parties that may set cookies include:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Google:</strong> If you sign in with Google, Google may set cookies related to authentication</li>
                <li><strong>PostHog:</strong> Analytics cookies as described above</li>
                <li><strong>Cloudinary:</strong> May set cookies when loading images</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">5. Local Storage</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                In addition to cookies, we use browser local storage to store certain information on your device. This includes your authentication token, user preferences, and cached data to improve performance. Local storage data persists until you clear your browser data or log out of Parish.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">6. Managing Cookies</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Most web browsers allow you to control cookies through their settings. You can typically:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>View what cookies are stored on your device</li>
                <li>Delete all or specific cookies</li>
                <li>Block all or third-party cookies</li>
                <li>Set preferences for certain websites</li>
              </ul>
              
              <div className="bg-[#FFF8E7] border border-[#F5E6C8] rounded-lg p-4 mt-4">
                <p className="text-[#8B6914] text-sm">
                  <strong>Note:</strong> Blocking essential cookies may prevent Parish from functioning properly. You may not be able to log in or use certain features if essential cookies are blocked.
                </p>
              </div>

              <h3 className="font-semibold text-[#2D2A26] mt-6 mb-2">Browser-Specific Instructions</h3>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[var(--liturgical-primary)] hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-[var(--liturgical-primary)] hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[var(--liturgical-primary)] hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[var(--liturgical-primary)] hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">7. Do Not Track</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Some browsers have a "Do Not Track" feature that sends a signal to websites requesting that your browsing not be tracked. Parish does not currently respond to Do Not Track signals, as there is no industry standard for how to handle these requests.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">8. Updates to This Policy</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">9. Contact Us</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                If you have questions about our use of cookies, please contact us at:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 mt-4">
                <p className="text-[#4A4A4A]"><strong>Email:</strong> <a href="mailto:privacy@parish.com" className="text-[var(--liturgical-primary)] hover:underline">privacy@parish.com</a></p>
              </div>
            </section>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            See also: <Link to="/privacy" className="text-[var(--liturgical-primary)] hover:underline">Privacy Policy</Link> · <Link to="/terms" className="text-[var(--liturgical-primary)] hover:underline">Terms of Service</Link> · <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link> · <Link to="/marketplace-terms" className="text-[var(--liturgical-primary)] hover:underline">Marketplace Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
